import { Checkbox } from '@/components/ui/checkbox';
import {useRegistrationOperation} from '@/dashboard/patients/use-registration-operation';
import { searchInitials } from '@/dashboard/ui/SharedRecordSearch';
import GuardianConsentFields, {type GuardianConsentValues} from '@/dashboard/patients/GuardianConsentFields';
/**
 * AdoraCare -- the full-screen PATIENT / TABLET intake mode
 * (PATIENT-INTAKE-PREFILL-AND-EXPERIENCE-V1; founder findings F-1/F-2/F-5/F-7/
 * F-9; recomposed by PATIENT-INTAKE-TABLET-RECOMPOSITION-V1 per founder
 * findings T-1..T-7 -- PRESENTATION ONLY, the submit contract below is
 * byte-unchanged). This is the authenticated clinic-device surface the
 * receptionist hands to the patient. It renders ONLY the active intake
 * (Section 8): NO staff nav, NO patient/lead/billing lists, NO other patient
 * data, NO dashboards. It lives OUTSIDE AppLayout/DashboardLayout (App.tsx
 * top-level route) so no staff header or nav can render, and it reuses the
 * receptionist's authenticated session only (no new auth, no public token,
 * no QR).
 *
 * FLOW: the S-6 live checklist states where the patient is (T-2) -> the
 * details CARD confirms evidence-backed prefill via label: value rows with a
 * per-field Fix (T-1) -> every remaining question is ONE section: title +
 * chips/field (T-3) -> submit ONCE from the anchored bar (T-4/T-5) -> the
 * hand-back screen. A changed identity value becomes PROPOSED CORRECTION
 * EVIDENCE (zero silent overwrite; staff review) and lands on the review
 * hand-back. Refresh recovery: the completion requestId is minted once and
 * persisted per appointment (sessionStorage), so a refresh resumes the SAME
 * active session and a resubmit is idempotent server-side.
 *
 * VALIDATION (T-4 / S-8): a submit tap with unanswered required questions
 * never calls the server -- the exact sections get their beside-the-field
 * "Not answered" marker, focus moves to the first one, and the anchored bar
 * carries the remaining count. Server errors render VERBATIM in the anchored
 * bar (LES-009), never as a floating mid-form banner.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  completeIntakeSession,
  getIntakePrefill,
  getIntakeQuestionnaire,
  saveIntakeQuestionnaire,
  type CompleteIntakeResult,
  type IntakeCorrectionField,
  type IntakePrefillResult,
  type PreconsultQuestionSpec,
  type ProposedCorrectionInput,
} from '@/dashboard/api';
import { startIntakeSession, type IntakeStartChoice } from '@/dashboard/appointments/intake-start-api';
import {IntakeDoorSelectionView} from './IntakeDrawer';
import { safeErrorMessage } from '@/lib/portal-api';
import { AcButton } from '@/dashboard/ui';
import { ARRIVAL_GENDER_OPTIONS } from '@/dashboard/patients/register-wizard-model';
import { buildAgeView } from '@/lib/age';
import { formatIstDateDdMmYyyy, parseDdMmYyyyToIso } from '@/lib/datetime-ist';
import {
  HandbackScreen,
  IntakeConsentBlock,
  IntakeQuestionSection,
  IntakeSubmitBar,
  PatientDetailsCard,
} from './PatientIntakeScreens';
import {
  RegistrationContractSections,
  type RegistrationFieldValues,
} from './RegistrationContractSections';
import {
  buildQuestionnairePayload,
  saveThenComplete,
  type QuestionnaireAnswerState,
} from './preconsult-questionnaire-view';
import {
  needsLineFromServer,
  registrationNeedsLine,
} from './registration-field-plan';
import {
  ageDisplayText,
  buildProposedCorrections,
  complaintOnFileLine,
  CORRECTION_REASON_OPTIONS,
  detailsSectionComplete,
  EMPTY_FIELD_CHANGE,
  inlineEditReason,
  knownFieldConfirmHint,
  missingIntakeSections,
  missingRegistrationKeys,
  PATIENT_INTAKE_VOICE,
  PATIENT_MODE_COMPLAINT_OTHER_PLACEHOLDER,
  PATIENT_MODE_DESK_CONFIRM_COPY,
  PATIENT_MODE_INLINE_EDIT_NOTE,
  PATIENT_MODE_PENDING_PHONE_NOTE,
  PATIENT_MODE_PENDING_TITLE,
  PATIENT_MODE_PHONE_CHANGE_COPY,
  PATIENT_MODE_PHONE_CONFIRM_COPY,
  PATIENT_MODE_TITLE,
  phoneConfirmTitle,
  prefillReviewRows,
  presubmitSummary,
  stageForOutcome,
  submitLabelForMissing,
  type FieldChangeState,
  type IntakeVoice,
  type PatientModeStage,
  type PrefillReviewRow,
} from './patient-intake-mode-view';
import './patient-intake-mode.css';

const MINOR_AGE = 18;


function reqIdKey(appointmentId: number): string {
  return `ac_intake_reqid_${appointmentId}`;
}

/** A stable client-minted requestId, persisted per appointment so a browser
 *  refresh resumes the SAME completion (server-side idempotency). */
function useHeldRequestId(appointmentId: number, sessionId: number): string {
  return useMemo(() => {
    const key = `${reqIdKey(appointmentId)}:${sessionId}`;
    try {
      const existing = window.sessionStorage.getItem(key);
      if (existing) return existing;
      const minted = `intake-${appointmentId}-${window.crypto?.randomUUID?.() ?? String(Date.now())}`;
      window.sessionStorage.setItem(key, minted);
      return minted;
    } catch {
      return `intake-${appointmentId}-fallback`;
    }
  }, [appointmentId, sessionId]);
}

export interface PatientIntakeModeProps {
  /** WALK-3-REGISTRATION-CONTAINER-FIX-V1: the day-board modal hosts this
   *  SAME form-core. When given, the appointment id comes from the host, not
   *  the route param (the tablet page reads it from the URL as before). */
  readonly appointmentId?: number;
  /** The copy + phone affordance wrapper (PATIENT default; DESK inside the
   *  modal). One form-core, two thin voice wrappers (C2). */
  readonly voice?: IntakeVoice;
  /** Fired ONCE on a successful completion when EMBEDDED -- the host (the
   *  modal) closes and refetches the day board (C3). The standalone tablet
   *  page omits it and shows its terminal hand-back instead. */
  readonly onBack?: () => void;
  readonly onBusyChange?: (busy: boolean) => void;
  readonly onComplete?: (result: CompleteIntakeResult) => void;
}

export default function PatientIntakeMode({
  appointmentId: appointmentIdProp,
  voice = PATIENT_INTAKE_VOICE,
  onComplete,
  onBack,
  onBusyChange,
}: PatientIntakeModeProps = {}): JSX.Element {
  const params = useParams();
  // EMBEDDED = the modal host owns completion (no full-screen chrome, no
  // terminal hand-back here); the tablet page is the standalone driver.
  const embedded = onComplete !== undefined;
  const appointmentId = appointmentIdProp ?? Number(params.appointmentId);
  const operation = useRegistrationOperation(String(appointmentId));

  const [identityChecked, setIdentityChecked] = useState(false);
  const [guardian,setGuardian] = useState<GuardianConsentValues>({guardianName:'',guardianRelationship:'',consentGrantedBy:'',consentChannel:''});
  const [stage, setStage] = useState<PatientModeStage>('loading');
  useEffect(() => { onBusyChange?.(stage === 'submitting'); }, [stage, onBusyChange]);
  useEffect(() => () => onBusyChange?.(false), [onBusyChange]);
  const [prefill, setPrefill] = useState<IntakePrefillResult | null>(null);
  const requestId = useHeldRequestId(Number.isFinite(appointmentId) ? appointmentId : 0, prefill?.intakeSessionId ?? 0);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CompleteIntakeResult | null>(null);

  // Per-field change state (Fix open == a proposed correction in progress).
  const [changes, setChanges] = useState<Partial<Record<IntakeCorrectionField, FieldChangeState>>>({});
  // Create inputs (a new patient must provide these).
  const [mobile, setMobile] = useState('');
  const [gender, setGender] = useState('');
  // F6 (AUG-12-WALK-BLOCKERS-V1): the DOB is TYPED dd/mm/yyyy (the tenant's
  // grammar) -- state holds the typed string; the wire gets the parsed ISO.
  const [dob, setDob] = useState('');
  // FD-32: the SAME Age|DOB segmented pattern registration uses (parity).
  // F6: AGE-FIRST default -- the walk's fastest honest entry; DOB stays one
  // tap away (a known DOB flips the mode at prefill-seed time).
  const [ageMode, setAgeMode] = useState<'age' | 'dob'>('age');
  const [approxAge, setApproxAge] = useState('');
  const [city, setCity] = useState('');
  // FD-30 (UX LAW 1): a known number is CONFIRMED (last-4), never re-typed;
  // "Use a different number" reveals the input.
  const [phoneConfirmed, setPhoneConfirmed] = useState(false);
  const [phoneEditing, setPhoneEditing] = useState(false);
  // AUG-12-WALK-2-REGISTRATION-TRUTH-V1 (F3): the config-gated registration
  // extras -- rendered + gated ONLY when the org contract names them.
  const [relationName, setRelationName] = useState('');
  const [contactReuse, setContactReuse] = useState('');
  useEffect(() => { setContactReuse(''); }, [appointmentId]);
  const [occupation, setOccupation] = useState('');
  const [handlingCategory, setHandlingCategory] = useState('');
  // REGISTRATION-CONTRACT-COMPLETE-PREP-V1: the 20260808_1 contract fields
  // (contract 2.2/2.3/2.4). Title + Relation Title are config-backed
  // picklists (verbatim vocabularies, free-text degrade); state/country
  // open PREFILLED from the config defaults (TELANGANA / INDIA), editable.
  const [nameTitle, setNameTitle] = useState('');
  const [lastName, setLastName] = useState('');
  const [relationTitle, setRelationTitle] = useState('');
  const [stateField, setStateField] = useState('');
  const [pincode, setPincode] = useState('');
  const [addressDoor, setAddressDoor] = useState('');
  const [addressStreet, setAddressStreet] = useState('');
  const [addressArea, setAddressArea] = useState('');
  const [addressLandmark, setAddressLandmark] = useState('');
  const [addressTaluka, setAddressTaluka] = useState('');
  const [addressCountry, setAddressCountry] = useState('');
  // REGISTRATION-PARTHA-CONTRACT-V1 (Part C/D): the alternate-number owner
  // detail + the emergency-contact trio. Config-gated (render only when the
  // tenant contract names them). Phones normalize server-side (E.164).
  // PDC-FO-GAP-CLOSURE-V1 (G2): the optional email (never requirable, R-A).
  const [email, setEmail] = useState('');
  const [altMobile, setAltMobile] = useState('');
  const [altMobileRelation, setAltMobileRelation] = useState('');
  const [altMobileRelationName, setAltMobileRelationName] = useState('');
  const [emergencyContactName, setEmergencyContactName] = useState('');
  const [emergencyContactRelation, setEmergencyContactRelation] = useState('');
  const [emergencyContactPhone, setEmergencyContactPhone] = useState('');
  // Contract 2.4: the optional tail of the address block sits behind ONE
  // expander; it opens itself when a stored value would otherwise hide.
  const [addressExpanded, setAddressExpanded] = useState(false);
  // FD-40: the patient's stated reason-for-visit (chip pick mirrors into
  // the ONE free-text value; the desk's capture is confirmed, never
  // re-asked).
  const [complaint, setComplaint] = useState('');
  // Pre-consultation questionnaire (server-resolved spec + patient answers).
  const [questions, setQuestions] = useState<PreconsultQuestionSpec[]>([]);
  // D5: the answer state now holds ONE key at most -- the reason_for_visit
  // text synthesized from the chip row at submit time. Every other question
  // is the doctor block's, so nothing else is ever set here.
  const [answers] = useState<QuestionnaireAnswerState>({});
  const submittingRef = useRef(false);
  // A successful answer save remains proved if completion loses its response.
  // Keep only an in-memory payload signature, never patient text in storage.
  const savedQuestionnaire = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!Number.isFinite(appointmentId) || appointmentId < 1) {
      setStage('error');
      setError('This intake link is not valid.');
      return;
    }
    (async () => {
      try {
        // Ensure an active session exists (idempotent start), then prefill.
        // The identity door (IDENTITY-LINK-AT-INTAKE-V1) may answer that the
        // desk must pick the person first ('selection_required', zero writes,
        // NO session): the candidate pick is a FRONT-DESK act, so this
        // patient-facing surface renders only a plain hand-back message --
        // never another patient's details.
        const started = await startIntakeSession(appointmentId, 'digital').catch(
          () => undefined,
        );
        if (started !== undefined && started.outcome === 'selection_required') {
          if (!cancelled) {
            setStage('error');
            setError(PATIENT_MODE_DESK_CONFIRM_COPY);
          }
          return;
        }
        const p = await getIntakePrefill(appointmentId);
        if (cancelled) return;
        setPrefill(p);
        // Seed create inputs from any known record -- linked patient OR the
        // desk's registration capture on the lead (D2: same record, read
        // back). A known DOB seeds the typed dd/mm/yyyy grammar and flips
        // the segmented mode; a known bare age seeds Age-first (F6).
        const dobField = p.fields.find((f) => f.field === 'dob');
        const genderField = p.fields.find((f) => f.field === 'gender');
        const cityField = p.fields.find((f) => f.field === 'city');
        if (dobField?.displayValue) {
          const typed = formatIstDateDdMmYyyy(dobField.displayValue);
          setDob(typed !== '' ? typed : dobField.displayValue);
          setAgeMode('dob');
        } else if (p.age.ageYears !== null) {
          setApproxAge(String(p.age.ageYears));
        }
        // FD-32: the registration vocabulary has three REAL answers; a
        // stored 'unknown' stays unanswered (it IS unknown).
        if (
          genderField?.displayValue &&
          genderField.displayValue !== 'unknown'
        ) {
          setGender(genderField.displayValue);
        }
        if (cityField?.displayValue) setCity(cityField.displayValue);
        // WALK-3 (C5): the DESK registration path prefills the person's OWN
        // number into the ONE editable phone field, reusing the SAME lawful
        // phoneFull wire (no second source, no duplicate field). The patient
        // voice keeps its confirm-don't-re-collect display, so mobile stays
        // unseeded there. NOTE: phoneFull is null for a lead whose plaintext
        // number was never stored (the lead-capture privacy rule keeps only
        // the fingerprint), so the field prefills only when the wire carries a
        // number -- the desk types it otherwise (run record C5 STOP item).
        if (voice.phoneMode === 'edit' && p.phoneFull) setMobile(p.phoneFull);
        // F3: prefill the config-gated extras from the desk's capture.
        if (p.registrationRelationName) setRelationName(p.registrationRelationName);
        if (p.registrationGuardian) setGuardian({guardianName:p.registrationGuardian.name,
          guardianRelationship:p.registrationGuardian.relationship,consentGrantedBy:p.registrationGuardian.consentGrantedBy??'',
          consentChannel:p.registrationGuardian.consentChannel??''});
        if (p.registrationContacts) {
          setAltMobile(p.registrationContacts.altMobile ?? '');
          setAltMobileRelation(p.registrationContacts.altMobileRelation ?? '');
          setAltMobileRelationName(p.registrationContacts.altMobileRelationName ?? '');
          setEmergencyContactName(p.registrationContacts.emergencyContactName ?? '');
          setEmergencyContactRelation(p.registrationContacts.emergencyContactRelation ?? '');
          setEmergencyContactPhone(p.registrationContacts.emergencyContactPhone ?? '');
        }

        // 8.6 RULING 3: a stored occupation seeds the picklist only when it
        // is IN the configured vocabulary -- an off-list legacy value would
        // dead-end at the writer's vocabulary validation, so the desk
        // re-picks instead (the stored value stays on the lead until the
        // new pick lands). Free-text mode (empty vocabulary) seeds as
        // before.
        if (p.registrationOccupation) {
          const occupationVocabulary = p.occupationOptions ?? [];
          if (
            p.registrationRecovery || occupationVocabulary.length === 0 ||
            occupationVocabulary.includes(p.registrationOccupation)
          ) {
            setOccupation(p.registrationOccupation);
          }
        }
        if (p.registrationHandlingCategory) {
          setHandlingCategory(p.registrationHandlingCategory);
        }
        // The 20260808_1 contract fields. The two title picklists take the
        // SAME vocabulary-guarded seed occupation takes (an off-list
        // legacy value re-picks instead of dead-ending at the writer's
        // validation). LEGACY RELATION FALLBACK (contract 8.1): a
        // pre-split lead carries only registrationRelationName -- that
        // combined value stays VERBATIM in the name field (seeded above)
        // and the title stays unpicked; nothing parses a split.
        const seedPick = (
          stored: string | null | undefined,
          vocabulary: readonly string[] | undefined,
          set: (v: string) => void,
        ): void => {
          if (!stored) return;
          const vocab = vocabulary ?? [];
          if (p.registrationRecovery || vocab.length === 0 || vocab.includes(stored)) set(stored);
        };
        seedPick(p.registrationNameTitle, p.nameTitleOptions, setNameTitle);
        seedPick(
          p.registrationRelationTitle,
          p.relationTitleOptions,
          setRelationTitle,
        );
        if (p.registrationLastName) setLastName(p.registrationLastName);
        // State / Country: the stored capture wins; the config default
        // (contract 2.3/2.4: TELANGANA / INDIA) prefills otherwise --
        // a PREFILL only, fully editable, stored only on submit.
        setStateField(p.registrationState ?? (p.registrationRecovery ? '' : p.stateDefault) ?? '');
        setAddressCountry(p.registrationAddressCountry ?? (p.registrationRecovery ? '' : p.countryDefault) ?? '');
        if (p.registrationPincode) setPincode(p.registrationPincode);
        if (p.registrationAddressDoor) setAddressDoor(p.registrationAddressDoor);
        if (p.registrationAddressStreet) {
          setAddressStreet(p.registrationAddressStreet);
        }
        if (p.registrationAddressArea) setAddressArea(p.registrationAddressArea);
        if (p.registrationAddressLandmark) {
          setAddressLandmark(p.registrationAddressLandmark);
        }
        if (p.registrationAddressTaluka) {
          setAddressTaluka(p.registrationAddressTaluka);
        }
        // A stored value inside the collapsed expander would be invisible
        // -- open it so the desk sees what is already on file.
        if (
          p.registrationAddressDoor ||
          p.registrationAddressStreet ||
          p.registrationAddressLandmark ||
          p.registrationAddressTaluka
        ) {
          if (voice.identityLayout !== 'fields') setAddressExpanded(true);
        }
        // F1 (the visible confirm surface): pending proposed changes from an
        // earlier submit pre-open their inline editors seeded with the
        // proposed values -- the desk reviews, edits if needed, and the ONE
        // Complete Registration action confirms. A phone proposal carries no
        // value by law -- open the phone editor for a fresh confirm instead.
        for (const pc of p.pendingCorrections ?? []) {
          if (pc.field === 'phone') {
            setPhoneEditing(true);
          } else if (
            pc.field === 'full_name' ||
            pc.field === 'dob' ||
            pc.field === 'external_mr'
          ) {
            setChanges((prev) => ({
              ...prev,
              [pc.field as IntakeCorrectionField]: {
                changed: true,
                proposedValue: pc.proposedValue ?? '',
                reason:
                  (pc.reason as FieldChangeState['reason']) ||
                  inlineEditReason(pc.field as IntakeCorrectionField),
              },
            }));
          }
        }
        // Load the question spec (server-resolved, org-aware) purely to learn
        // whether the org's set carries a required `reason_for_visit` -- the
        // ONE question this surface still asks (the chip row below).
        // D5: the ANSWER rehydrate is gone with the clinical block. No
        // clinical question renders here, so there is nothing to re-seed;
        // rehydrating answers the form cannot show would only risk writing
        // them back untouched.
        try {
          const q = await getIntakeQuestionnaire(appointmentId);
          if (!cancelled) {
            setQuestions(q.questions ?? []);
          }
        } catch {
          // A question-spec load fault leaves the form empty; the submit
          // still fails closed if there are questions to persist.
          if (!cancelled) setQuestions([]);
        }
        setStage('review');
      } catch (e) {
        if (cancelled) return;
        setStage('error');
        setError(
          safeErrorMessage(
            e,
            embedded
              ? 'Could not load the registration.'
              : 'Could not load your details.',
          ),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [appointmentId, voice.phoneMode, embedded]);

  const reviewRows = useMemo(() => (prefill ? prefillReviewRows(prefill) : []), [prefill]);
  const nameRow = reviewRows.find((r) => r.field === 'full_name') ?? null;
  /** D6 -- KILL THE DOUBLE RENDER. A field that has its own editable control
   *  below must NOT also appear as a read-back row in the details card:
   *  gender rendered as a summary row AND a chip section, city rendered as a
   *  summary row with no control at all, and the date of birth rendered as a
   *  row beside the Age row beside the Age|DOB section -- three renders of
   *  one fact. The card now carries exactly the two confirm-style identity
   *  rows (name, phone); everything else is its own single control. */
  const detailRows = useMemo(
    () =>
      reviewRows.filter(
        (r) => r.field === 'full_name' || r.field === 'phone',
      ),
    [reviewRows],
  );

  const dobUnknown = ageMode === 'age';
  // F6: the typed dd/mm/yyyy string parses through THE ONE inverse of the
  // dd/mm formatter; an unparsed-yet value is simply "no DOB yet".
  const dobIso = useMemo(
    () => (dob.trim() !== '' ? parseDdMmYyyyToIso(dob) : null),
    [dob],
  );
  const ageView = useMemo(
    () =>
      buildAgeView(
        dobUnknown ? null : dobIso,
        dobUnknown && approxAge.trim() !== '' ? Number(approxAge) : null,
        Date.now(),
        prefill?.clinicTimezone ?? null,
      ),
    [dobUnknown, dobIso, approxAge, prefill],
  );

  const isMinor = ageView.ageYears !== null && ageView.ageYears < MINOR_AGE;
  const guardianIncomplete = isMinor && Object.values(guardian).some(value=>!value.trim());

  const setChange = useCallback((field: IntakeCorrectionField, patch: Partial<FieldChangeState>) => {
    setChanges((prev) => ({
      ...prev,
      [field]: { ...(prev[field] ?? EMPTY_FIELD_CHANGE), ...patch },
    }));
  }, []);

  const proposedCorrections: ProposedCorrectionInput[] = useMemo(
    () => buildProposedCorrections(changes),
    [changes],
  );

  // D5 (REGISTRATION-CONTRACT-AND-WALK-CLOSURE-V1) -- THE CLINICAL
  // RELOCATION. The "A few questions for the doctor" block (when did it
  // start / pain-discomfort / the rest of that group) NO LONGER RENDERS HERE.
  // Registration is an ADMINISTRATIVE act at the desk; those questions are
  // CLINICAL context and now open the doctor's Start Consultation
  // (ConsultIntakeQuestionsBlock), reading and writing the SAME
  // patient_intake answers through the SAME routes. Nothing was deleted --
  // the question DEFINITIONS stay exactly where they were (the org's
  // preconsult.question_set config), so the future patient self-fill tier
  // renders them unchanged.
  //
  // The ONE question that stays at the desk is `reason_for_visit`: the
  // founder-kept single chip row below IS that answer (F7's merge, retained),
  // so the questionnaire is still SATISFIED by registration -- it is simply
  // no longer ASKED here twice.
  const reasonOnlyQuestions = useMemo(
    () => questions.filter((q) => q.key === 'reason_for_visit'),
    [questions],
  );
  // No clinical question renders on this surface, so none can be "missing"
  // here. The doctor's block owns their completeness.
  const questionnaireMissing: readonly { key: string }[] = [];

  const confirmedName =
    changes.full_name?.changed && changes.full_name.proposedValue.trim() !== ''
      ? changes.full_name.proposedValue.trim()
      : nameRow?.displayValue ?? '';

  // F5 (ONE PRIMARY ACTION): the read-back rows ARE the confirmation; the
  // section is incomplete only while an opened Fix editor is unfinished.
  // Client-render only; contract untouched.
  const detailsComplete = useMemo(
    () => detailsSectionComplete(changes),
    [changes],
  );

  // T-4 + the corrective: focus AFTER React renders the target (the
  // CheckinIdentityStep D3 focus-after-render pattern) -- a just-opened Fix
  // editor exists in the DOM by the time this effect runs. The target id may
  // be a control (focused directly) or a section (its first enabled control).
  const [pendingFocus, setPendingFocus] = useState<string | null>(null);
  useEffect(() => {
    if (pendingFocus === null) return;
    setPendingFocus(null);
    if (typeof document === 'undefined') return;
    const host = document.getElementById(pendingFocus);
    if (host === null) return;
    const target = host.matches('input, select, textarea, button')
      ? (host as HTMLElement)
      : host.querySelector<HTMLElement>(
          'input:not([disabled]), select:not([disabled]), button:not([disabled]), textarea',
        );
    if (target !== null) {
      target.focus();
      if (typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }
  }, [pendingFocus]);

  // The unanswered required sections, in visual order (the ONE pure derivation
  // the pins share).
  const phoneLast4 = prefill?.phoneLast4 ?? null;
  // F8 (founder JIT ruling): the patient's OWN number, full, on their own
  // screen. On file (full or lawful masked staff fallback) = SATISFIED --
  // the re-type-to-confirm ask is CUT; typing remains only the door for a
  // NEW number.
  const phoneFull = prefill?.phoneFull ?? null;
  const phoneHasValueOnFile =
    prefill?.fields.find((f) => f.field === 'phone')?.hasValue === true;
  const phoneOnFileSatisfied = !phoneEditing && (phoneFull !== null || phoneHasValueOnFile);
  const genderRequired = (prefill?.genderRequired ?? true) || (prefill?.registrationRequiredFields?.includes('gender') ?? false);
  // F7 (the merged reason section): the question set's required
  // reason_for_visit is ANSWERED BY the "What brings you in today?" section
  // -- one ask, one required signal; the desk's captured reason satisfies it.
  const reasonQuestion = useMemo(
    () => questions.find((q) => q.key === 'reason_for_visit') ?? null,
    [questions],
  );
  const complaintOnFile = prefill?.complaintOnFile ?? null;

  // ONE-REGISTRATION-MODULE: this host adapts its flat intake state into the
  // shared RegistrationFieldValues shape + a setter-dispatching onChange, so the
  // governed contract sections render from THE ONE module (RegistrationContractSections).
  const registrationValues: RegistrationFieldValues = {
    contactReuse,
    nameTitle,
    lastName,
    ageMode,
    approxAge,
    dob,
    gender,
    email,
    altMobile,
    altMobileRelation,
    altMobileRelationName,
    relationTitle,
    relationName,
    city,
    addressArea,
    state: stateField,
    pincode,
    addressDoor,
    addressStreet,
    addressLandmark,
    addressTaluka,
    addressCountry,
    addressExpanded,
    occupation,
    handlingCategory,
    emergencyContactName,
    emergencyContactRelation,
    emergencyContactPhone,
  };
  useEffect(() => { setIdentityChecked(false); }, [confirmedName, nameTitle, lastName, mobile, ageMode, dob, approxAge, gender, prefill?.patientId, appointmentId]);
  const onRegistrationChange = (patch: Partial<RegistrationFieldValues>): void => {
    if (patch.contactReuse !== undefined) setContactReuse(patch.contactReuse);
    if (patch.nameTitle !== undefined) setNameTitle(patch.nameTitle);
    if (patch.lastName !== undefined) setLastName(patch.lastName);
    if (patch.ageMode !== undefined) setAgeMode(patch.ageMode);
    if (patch.approxAge !== undefined) setApproxAge(patch.approxAge);
    if (patch.dob !== undefined) setDob(patch.dob);
    if (patch.gender !== undefined) setGender(patch.gender);
    if (patch.email !== undefined) setEmail(patch.email);
    if (patch.altMobile !== undefined) setAltMobile(patch.altMobile);
    if (patch.altMobileRelation !== undefined)
      setAltMobileRelation(patch.altMobileRelation);
    if (patch.altMobileRelationName !== undefined)
      setAltMobileRelationName(patch.altMobileRelationName);
    if (patch.relationTitle !== undefined) setRelationTitle(patch.relationTitle);
    if (patch.relationName !== undefined) setRelationName(patch.relationName);
    if (patch.city !== undefined) setCity(patch.city);
    if (patch.addressArea !== undefined) setAddressArea(patch.addressArea);
    if (patch.state !== undefined) setStateField(patch.state);
    if (patch.pincode !== undefined) setPincode(patch.pincode);
    if (patch.addressDoor !== undefined) setAddressDoor(patch.addressDoor);
    if (patch.addressStreet !== undefined) setAddressStreet(patch.addressStreet);
    if (patch.addressLandmark !== undefined)
      setAddressLandmark(patch.addressLandmark);
    if (patch.addressTaluka !== undefined) setAddressTaluka(patch.addressTaluka);
    if (patch.addressCountry !== undefined)
      setAddressCountry(patch.addressCountry);
    if (patch.addressExpanded !== undefined)
      setAddressExpanded(patch.addressExpanded);
    if (patch.occupation !== undefined) setOccupation(patch.occupation);
    if (patch.handlingCategory !== undefined)
      setHandlingCategory(patch.handlingCategory);
    if (patch.emergencyContactName !== undefined)
      setEmergencyContactName(patch.emergencyContactName);
    if (patch.emergencyContactRelation !== undefined)
      setEmergencyContactRelation(patch.emergencyContactRelation);
    if (patch.emergencyContactPhone !== undefined)
      setEmergencyContactPhone(patch.emergencyContactPhone);
  };
  const complaintMissing =
    reasonQuestion !== null &&
    reasonQuestion.required &&
    complaintOnFile === null &&
    complaint.trim() === '';
  const registrationRequiredFields = useMemo(
    () => prefill?.registrationRequiredFields ?? [],
    [prefill],
  );
  // D2/D3: ONE fact bundle feeds BOTH the focus targets and the needs-line,
  // so the sentence the desk reads and the control the form jumps to can
  // never disagree.
  const gateFacts = useMemo(
    () => ({
      detailsComplete,
      confirmedName,
      mobile,
      gender,
      missingQuestionKeys: questionnaireMissing.map((q) => q.key),
      phoneConfirmed,
      phoneConfirmAvailable: phoneLast4 !== null,
      phoneOnFileSatisfied,
      genderRequired,
      complaintMissing,
      registrationRequiredFields,
      relationName,
      occupation,
      handlingCategory,
      city,
      nameTitle,
      lastName,
      relationTitle,
      stateField,
      pincode,
      addressDoor,
      addressStreet,
      addressArea,
      addressLandmark,
      addressTaluka,
      addressCountry,
      altMobile,
      altMobileRelation,
      altMobileRelationName,
      emergencyContactName,
      emergencyContactRelation,
      emergencyContactPhone,
      ageKnown: ageView.ageYears !== null,
    }),
    [
      detailsComplete,
      confirmedName,
      mobile,
      gender,
      questionnaireMissing,
      phoneConfirmed,
      phoneLast4,
      phoneOnFileSatisfied,
      genderRequired,
      complaintMissing,
      registrationRequiredFields,
      relationName,
      occupation,
      handlingCategory,
      city,
      nameTitle,
      lastName,
      relationTitle,
      stateField,
      pincode,
      addressDoor,
      addressStreet,
      addressArea,
      addressLandmark,
      addressTaluka,
      addressCountry,
      altMobile,
      altMobileRelation,
      altMobileRelationName,
      emergencyContactName,
      emergencyContactRelation,
      emergencyContactPhone,
      ageView.ageYears,
    ],
  );
  const missingSectionIds = useCallback(
    (): string[] => missingIntakeSections(gateFacts),
    [gateFacts],
  );
  /** D3 -- THE ONE VALIDATION SURFACE. Every currently-missing required
   *  field, named in desk voice, in one sticky line. No second error style,
   *  no per-field technical message, no raw wire key. */
  const missingKeys = useMemo(
    () => missingRegistrationKeys(gateFacts),
    [gateFacts],
  );
  const needsLine = registrationNeedsLine(missingKeys);

  async function recoverSavedRegistration(): Promise<void> {
    const recovery = prefill?.registrationRecovery;
    if (!recovery?.canResume || !identityChecked || submittingRef.current) return;
    submittingRef.current = true;setStage('submitting');setError(null);
    try {
      const bearer = await operation.begin();
      const res = await completeIntakeSession(appointmentId, {requestId:recovery.requestId,identityChecked:true,recoverSavedRegistration:true}, bearer);
      await operation.check(bearer);
      if (res.doctorQueueReady && onComplete) {onComplete(res);return;}
      setError(res.checkIn?.warning ?? 'Registration saved. Check-in is still pending.');setStage('review');
    } catch (e) {
      if (!operation.isCurrent()) return;
      setError(safeErrorMessage(e,'Could not finish check-in. The saved registration is kept.'));setStage('review');
    } finally {submittingRef.current=false;}
  }

  async function onSubmit(patientChoice?: IntakeStartChoice): Promise<void> {
    if (submittingRef.current) return;
    if (guardianIncomplete) {setError('Complete guardian and consent details.');return;}
    if (voice.identityLayout === 'fields' && !identityChecked) { setError('Check the selected patient identity before checking in.'); return; }
    submittingRef.current = true;
    setStage('submitting');
    setError(null);
    try {
      const bearer = await operation.begin();
      // FAIL CLOSED (founder corrective): persist the pre-consultation answers
      // FIRST and prove success, THEN run the UNCHANGED completion engine.
      // saveThenComplete calls completeIntakeSession ONLY if the save resolves;
      // a persistence failure throws here and completion is NEVER invoked -- no
      // link, no auto-check-in, no queue progression. The entered answers stay
      // in state, so a retry re-submits them (idempotent server-side upsert).
      // F7: the merged reason section IS the reason_for_visit answer -- the
      // patient's text/chip, else the desk's on-file reason they confirmed.
      const reasonText =
        complaint.trim() !== '' ? complaint.trim() : (complaintOnFile ?? '');
      const answersForPayload =
        reasonQuestion !== null && reasonText !== ''
          ? { ...answers, reason_for_visit: { text: reasonText } }
          : answers;
      // D5: registration persists ONLY the reason it actually asked. The
      // clinical questions are not on this surface, so submitting an empty
      // answer for them here would write a silent "unanswered" over what the
      // doctor's block is about to collect.
      const questionnairePayload = buildQuestionnairePayload(
        reasonOnlyQuestions,
        answersForPayload,
      );
      // FD-32 parity: an unanswered optional gender maps to the stored
      // 'unknown' exactly as registration's wire does.
      const effectiveGender = gender.trim() !== '' ? gender.trim() : 'unknown';
      // F8: the person's on-file number satisfies identity matching without a
      // re-type -- a typed number wins only when the patient changed it.
      const effectiveMobile = mobile.trim() !== '' ? mobile.trim() : (phoneFull ?? '');
      const fields =
        confirmedName.trim() !== '' && (effectiveMobile !== '' || prefill?.hasLinkedPatient === true)
          ? {
              fullName: confirmedName.trim(),
              ...(isMinor?{guardian:{name:guardian.guardianName,relationship:guardian.guardianRelationship},
                consent:{grantedBy:guardian.consentGrantedBy,channel:guardian.consentChannel}}:{}),
              ...(effectiveMobile !== '' ? {mobile: effectiveMobile} : {}),
              gender: effectiveGender,
              ...(dobUnknown
                ? approxAge.trim() !== ''
                  ? { ageYears: Number(approxAge) }
                  : {}
                : dobIso !== null
                  ? { dob: dobIso }
                  : {}),
              ...(city.trim() !== '' ? { city: city.trim() } : {}),
              // F3: the config-gated extras ride when given -- the ONE
              // Complete Registration action must satisfy the org contract.
              ...(relationName.trim() !== ''
                ? { relationName: relationName.trim() }
                : {}),
              ...(occupation.trim() !== '' ? { occupation: occupation.trim() } : {}),
              ...(handlingCategory.trim() !== ''
                ? { handlingCategory: handlingCategory.trim() }
                : {}),
              // The 20260808_1 contract fields ride the SAME way -- only
              // when given; the writer validates the picklist values.
              ...(nameTitle.trim() !== '' ? { nameTitle: nameTitle.trim() } : {}),
              ...(lastName.trim() !== '' ? { lastName: lastName.trim() } : {}),
              ...(relationTitle.trim() !== ''
                ? { relationTitle: relationTitle.trim() }
                : {}),
              ...(stateField.trim() !== '' ? { state: stateField.trim() } : {}),
              ...(pincode.trim() !== '' ? { pincode: pincode.trim() } : {}),
              ...(addressDoor.trim() !== ''
                ? { addressDoor: addressDoor.trim() }
                : {}),
              ...(addressStreet.trim() !== ''
                ? { addressStreet: addressStreet.trim() }
                : {}),
              ...(addressArea.trim() !== ''
                ? { addressArea: addressArea.trim() }
                : {}),
              ...(addressLandmark.trim() !== ''
                ? { addressLandmark: addressLandmark.trim() }
                : {}),
              ...(addressTaluka.trim() !== ''
                ? { addressTaluka: addressTaluka.trim() }
                : {}),
              ...(addressCountry.trim() !== ''
                ? { addressCountry: addressCountry.trim() }
                : {}),
              // PDC-FO-GAP-CLOSURE-V1 (G2): the optional email rides when
              // given; THE ONE writer-side parser validates the shape.
              ...(email.trim() !== '' ? { email: email.trim() } : {}),
              // PARTHA-CONTRACT Part C/D: the alt-number owner detail + the
              // emergency-contact trio ride when given; the writer normalizes
              // the phones (E.164) and validates the relations.
              ...(altMobile.trim() !== '' ? { altMobile: altMobile.trim() } : {}),
              ...(altMobileRelation.trim() !== ''
                ? { altMobileRelation: altMobileRelation.trim() }
                : {}),
              ...(altMobileRelationName.trim() !== ''
                ? { altMobileRelationName: altMobileRelationName.trim() }
                : {}),
              ...(emergencyContactName.trim() !== ''
                ? { emergencyContactName: emergencyContactName.trim() }
                : {}),
              ...(emergencyContactRelation.trim() !== ''
                ? { emergencyContactRelation: emergencyContactRelation.trim() }
                : {}),
              ...(emergencyContactPhone.trim() !== ''
                ? { emergencyContactPhone: emergencyContactPhone.trim() }
                : {}),
            }
          : undefined;
      const res = await saveThenComplete({
        hasQuestionnaire: reasonOnlyQuestions.length > 0 && savedQuestionnaire.current !== JSON.stringify([appointmentId, questionnairePayload]),
        save: async () => {
          await operation.check(bearer);
          await saveIntakeQuestionnaire(appointmentId, { answers: questionnairePayload }, bearer);
          await operation.check(bearer);
          savedQuestionnaire.current = JSON.stringify([appointmentId, questionnairePayload]);
        },
        complete: async () => {
          await operation.check(bearer);
          return completeIntakeSession(appointmentId, {
            requestId,
            identityChecked,
            ...(patientChoice ? {patientChoice} : {}),
            registrationVersion: prefill?.registrationVersion,
            ...(fields ? { fields } : {}),
            ...(proposedCorrections.length > 0 ? { proposedCorrections } : {}),
            // FD-40: the stated reason rides only when given and only when
            // the desk captured none (write-if-empty server-side).
            ...(complaint.trim() !== '' && (prefill?.complaintOnFile ?? null) === null
              ? { complaint: complaint.trim() }
              : {}),
          }, bearer);
        },
      });
      await operation.check(bearer);
      // F1/F3 (AUG-12-WALK-2-REGISTRATION-TRUTH-V1): an unsatisfied org
      // contract is NOT a hand-back -- the bar names the exact missing
      // fields and the form stays live so the desk finishes them here.
      if (res.outcome === 'registration_capture_required') {
        // D3: the server's answer enters the SAME needs-line vocabulary --
        // one validation surface, one sentence shape, whether the gap was
        // caught on the client or reported by the contract check.
        const missing = res.missingRegistrationFields ?? [];
        setError(
          needsLineFromServer(missing) ??
            res.warnings[0] ??
            'Registration is not complete yet.',
        );
        setStage('review');
        return;
      }
      if (embedded && !res.doctorQueueReady && res.requiredAction !== 'mark_checked_in') {
        setResult(res);
        setError(res.warnings.join(' ') || (res.requiredAction === 'review_identity'
          ? 'Confirm who this visit is for. Your entered registration details are kept.'
          : 'Registration is not complete. Review the saved identity before checking in. Your entered details are kept.'));
        setStage('review');
        return;
      }
      if (embedded && res.requiredAction === 'mark_checked_in' && !res.doctorQueueReady) {
        setError(res.checkIn?.warning ?? 'Registration saved. Check-in is still pending. Return to the patient and retry check-in.')
        setStage('review');
        return;
      }
      setResult(res);
      // WALK-3 (C3): an EMBEDDED completion returns to the host -- the modal
      // closes and refetches the day board, whose row then DERIVES "Ready for
      // doctor" from the existing evaluator (no new state). The standalone
      // tablet page falls through to its terminal hand-back stage instead.
      if (embedded && onComplete && res.doctorQueueReady) {
        onComplete(res);
        return;
      }
      setStage(stageForOutcome(res.outcome));
    } catch (e) {
      if (!operation.isCurrent()) return;
      setError(
        safeErrorMessage(
          e,
          embedded
            ? 'Could not submit the registration.'
            : 'Could not submit your details.',
        ),
      );
      setStage('review');
    } finally {
      submittingRef.current = false;
    }
  }

  // The submit TAP (F5 -- ONE PRIMARY ACTION): the single submit is DISABLED
  // until every required section is complete, so this guard is a belt --
  // never a second flow. No mid-form second submit exists.
  async function onSubmitTap(): Promise<void> {
    if (missingSectionIds().length > 0) return;
    await onSubmit();
  }

  // --- Render ---------------------------------------------------------------

  // WALK-3 (C1): EMBEDDED renders WITHOUT the full-screen tablet chrome (the
  // modal IS the container) and WITHOUT the .pim tablet type-ramp (the desk is
  // a staff screen, not a handed-over tablet). The standalone page keeps the
  // full-screen .pim-shell / .pim exactly as before.
  const shell = (children: JSX.Element) =>
    embedded ? (
      <div data-testid="patient-intake-mode" className="pim-embed">
        {children}
      </div>
    ) : (
      <div data-testid="patient-intake-mode" className="pim-shell">
        <div className="pim">{children}</div>
      </div>
    );

  if (stage === 'loading') {
    // WALK-3 (C2): the staff path carries zero patient copy, in transient
    // states too -- the desk sees neutral loading/error text, the patient page
    // keeps its second-person copy byte-identical.
    return shell(<p>{embedded ? 'Loading the registration...' : 'Loading your details...'}</p>);
  }
  if (stage === 'error') {
    return shell(
      <div className="pim-card pim-handback">
        <h1>{embedded ? 'Could not start the registration' : 'We could not start your intake'}</h1>
        <p>
          {error ??
            (embedded
              ? 'Please try again, or close and reopen the registration.'
              : 'Please return this device to the receptionist.')}
        </p>
      </div>,
    );
  }
  if (stage === 'done') {
    return shell(<HandbackScreen reviewRequired={false} checkedIn={result?.doctorQueueReady ?? false} />);
  }
  if (stage === 'review_required') {
    return shell(<HandbackScreen reviewRequired checkedIn={false} />);
  }

  const submitting = stage === 'submitting';
  const recovery = embedded ? prefill?.registrationRecovery ?? null : null;
  const missingCount = missingSectionIds().length;

  // The per-field editor (T-1: Fix opens JUST that field), in TWO honest
  // shapes (AUG-12-WALK-2-REGISTRATION-TRUTH-V1 F5):
  //   - UNLINKED (pre-mint): a plain inline edit -- the change IS the
  //     registration capture (auto reason as provenance); no staff-confirm
  //     claim, no reason ask.
  //   - LINKED: the bounded correction editor (value + reason) -- canonical
  //     identity is never edited here; staff review stands.
  const hasLinkedPatient = prefill?.hasLinkedPatient === true;
  const fixEditor = (r: PrefillReviewRow): JSX.Element =>
    hasLinkedPatient ? (
      <>
        <p className="pim-fixnote">
          This differs from the appointment or patient record. A staff member will confirm it - nothing is
          changed automatically.
        </p>
        <input
          id={`pim-fix-input-${r.field}`}
          className="pim-input"
          aria-label={`New ${r.label}`}
          placeholder={`New ${r.label.toLowerCase()}`}
          value={changes[r.field as IntakeCorrectionField]?.proposedValue ?? ''}
          onChange={(e) =>
            setChange(r.field as IntakeCorrectionField, { proposedValue: e.target.value })
          }
        />
        <select
          className="pim-select"
          aria-label={`Reason for changing ${r.label}`}
          value={changes[r.field as IntakeCorrectionField]?.reason ?? ''}
          onChange={(e) =>
            setChange(r.field as IntakeCorrectionField, {
              reason: e.target.value as FieldChangeState['reason'],
            })
          }
        >
          <option value="">Why is it different?</option>
          {CORRECTION_REASON_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </>
    ) : (
      <>
        <p className="pim-fixnote">{PATIENT_MODE_INLINE_EDIT_NOTE}</p>
        <input
          id={`pim-fix-input-${r.field}`}
          className="pim-input"
          aria-label={`New ${r.label}`}
          placeholder={`New ${r.label.toLowerCase()}`}
          value={changes[r.field as IntakeCorrectionField]?.proposedValue ?? ''}
          onChange={(e) =>
            setChange(r.field as IntakeCorrectionField, {
              proposedValue: e.target.value,
              reason: inlineEditReason(r.field as IntakeCorrectionField),
            })
          }
        />
      </>
    );

  const openFixFields = new Set(
    Object.entries(changes)
      .filter(([, c]) => c?.changed)
      .map(([field]) => field),
  );

  // REGISTRATION-DEPLOYED-WALK-CLOSURE-V1 (founder visual verdict 2026-08-13):
  // the DESK modal IS the mockup contract, so identity renders AS FIELDS inside
  // the Patient details / Contact section cards (never a read-back summary
  // card). The patient tablet keeps its confirm / read-back CARD grammar.
  const identityAsFields = voice.identityLayout === 'fields' && prefill !== null;
  const toggleFix = (field: string): void => {
    const open = changes[field as IntakeCorrectionField]?.changed ?? false;
    setChange(field as IntakeCorrectionField, { changed: !open });
  };
  const pendingBanner =
    (prefill?.pendingCorrections ?? []).length > 0 ? (
      <div className="pim-pending" data-testid="intake-pending-corrections">
        <p className="pim-pending__title">{PATIENT_MODE_PENDING_TITLE}</p>
        {(prefill?.pendingCorrections ?? []).some((pc) => pc.field === 'phone') ? (
          <p className="pim-pending__line">{PATIENT_MODE_PENDING_PHONE_NOTE}</p>
        ) : null}
      </div>
    ) : null;
  // DESK Full name field (the mockup's First-name position). Repo truth: the
  // canonical registration name is `full_name` (register-wizard-model.ts), NOT a
  // separate first name -- so the field is Full name (documented FND). It is a
  // read-only field + the audited Fix door: an appointment-sourced identity name
  // is changed ONLY through the proposed-correction flow (linked = staff review
  // + reason; unlinked = inline capture), never free-typed -- the identity law.
  const nameFixOpen = openFixFields.has('full_name');
  const deskFullNameField =
    identityAsFields && nameRow !== null ? (
      <section
        id="pim-sec-details"
        className="pim-card pim-qcard"
        data-testid="intake-detail-full_name"
        data-reg-field="full_name"
      >
        <h2 className="pim-q">
          Full name
          <span className="pim-req" aria-hidden="true">
            *
          </span>
        </h2>
        <input
          className="pim-input"
          aria-label="Full name"
          value={confirmedName}
          readOnly
          data-testid="intake-value-full_name"
        />

      </section>
    ) : undefined;
  // DESK Mobile field (the mockup's Contact > Mobile position). The desk edits
  // the person's OWN number in place (phoneMode 'edit'), so it is a plain
  // editable field -- the SAME input the read-back card hosted, repositioned.
  const deskMobileField = identityAsFields ? (
    <section
      id="pim-sec-mobile"
      className="pim-card pim-qcard"
      data-testid="intake-detail-mobile"
      data-reg-field="mobile"
    >
      <h2 className="pim-q">
        Patient mobile
        <span className="pim-req" aria-hidden="true">
          *
        </span>
      </h2>
      {recovery ? <p className="pim-review__v" data-testid="intake-saved-mobile">
        {phoneFull ?? (phoneLast4 ? `•••• ${phoneLast4}` : phoneHasValueOnFile
          ? 'Saved — hidden in this view' : prefill?.phoneRevealState === 'no_value' ? 'Not supplied' : 'Saved details unavailable')}
      </p> : <input
        className="pim-input"
        aria-label="Mobile"
        type="tel"
        inputMode="tel"
        value={mobile}
        disabled={submitting}
        onChange={(e) => setMobile(e.target.value)}
        data-testid="intake-mobile"
      />}

    </section>
  ) : undefined;
  // DESK reason section: the mockup renders Reason for visit as a section card
  // with the small-caps eyebrow (not the tablet's big question heading). The
  // inner controls (chips + one-line free text, or the on-file line) are shared.
  const reasonInner =
    complaintOnFile !== null ? (
      <p className="pim-hint" data-testid="intake-complaint-line">
        {complaintOnFileLine(complaintOnFile)}
      </p>
    ) : (
      <>
        {(prefill?.complaintCategories ?? []).length > 0 ? (
          <div className="ac-tristate" role="group" aria-label="Reason for visit">
            {(prefill?.complaintCategories ?? []).map((c) => (
              <button
                key={c.code}
                type="button"
                className={
                  complaint === c.label
                    ? 'ac-tristate__option ac-tristate__option--on'
                    : 'ac-tristate__option'
                }
                aria-pressed={complaint === c.label}
                onClick={() => setComplaint(c.label)}
                data-testid={`intake-complaint-chip-${c.code}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        ) : null}
        <input
          className="pim-input"
          aria-label="Reason for visit - your own words"
          placeholder={PATIENT_MODE_COMPLAINT_OTHER_PLACEHOLDER}
          value={complaint}
          maxLength={200}
          onChange={(e) => setComplaint(e.target.value)}
          style={{ marginTop: 'var(--ac-space-2)' }}
          data-testid="intake-complaint-text"
        />
      </>
    );

  return shell(
    <>
      {/* WALK-3 (C1/C2): the second-person page title ("Please confirm your
          details") is PAGE-CHROME -- it renders only on the standalone tablet
          page. The modal supplies its own desk header (eyebrow "Registration"
          + the patient label), so the staff path carries zero patient copy. */}
      {embedded ? null : (
        <header className="pim-head">
          <h1 className="pim-title">{PATIENT_MODE_TITLE}</h1>
          {prefill?.clinicName ? (
            <p className="pim-context">
              {prefill.clinicName}
              {prefill.doctorName ? ` - ${prefill.doctorName}` : prefill.doctorNote ? ` - ${prefill.doctorNote}` : ''}
            </p>
          ) : null}
          {/* AUG-12-WALK-2-REGISTRATION-TRUTH-V1 (F6; founder walk W-4/W-5):
              the three-stage checklist is CUT from this surface -- since the
              F3 reshape this form IS the registration (the strip's
              "Registration - Reception - done" claim was no longer true), and
              the density pass wants one compact header, not a program. */}
        </header>
      )}

      {embedded && result?.outcome === 'identity_review_required' && result.requiredAction === 'review_identity' ?
        <IntakeDoorSelectionView showLastVisit={false}
          door={{candidates:result.candidates.map(candidate=>({...candidate,lastVisitAt:null})),bookedAppointments:[],reconciliationPending:result.warnings.length>0,warnings:result.warnings}}
          busy={submitting} onPick={patientId=>void onSubmit({action:'link',patientId})}
          onCreateNew={()=>void onSubmit({action:'create_new'})}/> : null}
      {identityAsFields ? <div className="arrival-identity-summary">
        <div className="arrival-identity-person"><span className="aps-avatar" aria-hidden="true">{searchInitials(confirmedName || 'Selected patient')}</span><div><strong>{confirmedName || 'Selected patient'}</strong><p>{prefill?.patientUmr ?? (recovery ? 'Registration saved' : 'Registration pending')} · {phoneFull ?? (prefill?.phoneLast4 ? `•••• ${prefill.phoneLast4}` : recovery ? 'Saved contact details kept' : 'Mobile not recorded')} · {ageDisplayText(ageView) ?? 'Age not recorded'}</p></div></div>
        <label><Checkbox className="arrival-identity-checkbox" checked={identityChecked} disabled={submitting} onCheckedChange={checked=>setIdentityChecked(checked)}/> Identity checked</label>
      </div> : null}
      {identityAsFields && nameRow && !recovery ? <details className="arrival-name-correction">
        <summary>Request a name correction</summary>
        <p className="pim-hint">Source: {nameRow.sourceLabel}</p>
        <button type="button" className="pim-fix" disabled={submitting} aria-expanded={nameFixOpen}
          onClick={()=>toggleFix('full_name')} data-testid="intake-fix-full_name">{nameFixOpen?'Keep as is':'Fix'}</button>
        {nameFixOpen ? <div className="pim-fixbody" data-testid="intake-fixbody-full_name">{fixEditor(nameRow)}</div> : null}
      </details> : null}
      {recovery ? <p role="status" className="pim-hint" data-testid="intake-saved-recovery">Registration saved. Check-in is still pending. Saved contact details remain on file.{!recovery.canResume ? ' The saved registration could not be verified. Close and reopen it to try again.' : ''}</p> : null}
      <fieldset className="pim-body" disabled={recovery !== null} style={{border:0,margin:0,padding:0}}>

        {/* REGISTRATION-DEPLOYED-WALK-CLOSURE-V1 (founder visual verdict
            2026-08-13): the DESK modal is the mockup contract, where identity
            renders AS FIELDS -- Full name in Patient details, Mobile in Contact
            (slotted into the governed module below), never a read-back summary
            card. The pending-corrections banner (when any) leads the body. The
            patient TABLET keeps the compact read-back details CARD (label:value
            rows + per-field tap-to-edit, its confirm grammar). */}
        {identityAsFields ? pendingBanner : null}
        {prefill && !identityAsFields ? (
          <PatientDetailsCard
            rows={detailRows}
            /* D6: the age READ-BACK moves into the Age|DOB control below --
               the card no longer states an age the section then re-asks. */
            ageText={null}
            heading={voice.detailsHeading}
            pendingBanner={
              (prefill.pendingCorrections ?? []).length > 0 ? (
                <div className="pim-pending" data-testid="intake-pending-corrections">
                  <p className="pim-pending__title">{PATIENT_MODE_PENDING_TITLE}</p>
                  {(prefill.pendingCorrections ?? []).some(
                    (pc) => pc.field === 'phone',
                  ) ? (
                    <p className="pim-pending__line">
                      {PATIENT_MODE_PENDING_PHONE_NOTE}
                    </p>
                  ) : null}
                </div>
              ) : null
            }
            phoneCell={
              <span id="pim-sec-mobile" className="pim-phone-cell">
                {voice.phoneMode === 'edit' ? (
                  // WALK-3 (C5): DESK -- the person's OWN number in ONE
                  // editable field, prefilled from the SAME phoneFull wire
                  // (seeded into mobile at load). No confirm chip, no second
                  // field; the desk edits in place. Empty only when no
                  // plaintext number is on file (the desk types it).
                  <input
                    className="pim-input"
                    aria-label="Phone"
                    type="tel"
                    inputMode="tel"
                    value={mobile}
                    disabled={submitting}
                    onChange={(e) => setMobile(e.target.value)}
                    data-testid="intake-mobile"
                  />
                ) : phoneFull !== null && !phoneEditing ? (
                  <>
                    <span className="pim-review__v" data-testid="intake-phone-full">
                      {phoneFull}
                    </span>
                    <button
                      type="button"
                      className="pim-fix"
                      disabled={submitting}
                      onClick={() => {
                        setPhoneEditing(true);
                        setPhoneConfirmed(false);
                      }}
                      data-testid="intake-phone-change"
                    >
                      {PATIENT_MODE_PHONE_CHANGE_COPY}
                    </button>
                  </>
                ) : phoneLast4 !== null && !phoneEditing ? (
                  <>
                    <span className="pim-review__v">{phoneConfirmTitle(phoneLast4)}</span>
                    <span className="ac-tristate" role="group" aria-label="Confirm mobile">
                      <button
                        type="button"
                        className={
                          phoneConfirmed
                            ? 'ac-tristate__option ac-tristate__option--on'
                            : 'ac-tristate__option'
                        }
                        aria-pressed={phoneConfirmed}
                        onClick={() => setPhoneConfirmed(true)}
                        data-testid="intake-phone-confirm"
                      >
                        {PATIENT_MODE_PHONE_CONFIRM_COPY}
                      </button>
                      <button
                        type="button"
                        className="ac-tristate__option"
                        onClick={() => {
                          setPhoneEditing(true);
                          setPhoneConfirmed(false);
                        }}
                        data-testid="intake-phone-change"
                      >
                        {PATIENT_MODE_PHONE_CHANGE_COPY}
                      </button>
                    </span>
                  </>
                ) : (
                  <input
                    className="pim-input"
                    aria-label="Mobile number"
                    type="tel"
                    inputMode="tel"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    data-testid="intake-mobile"
                  />
                )}
              </span>
            }
            openFixFields={openFixFields}
            busy={submitting}
            onToggleFix={(field) => {
              const open = changes[field as IntakeCorrectionField]?.changed ?? false;
              setChange(field as IntakeCorrectionField, { changed: !open });
            }}
            fixEditor={fixEditor}
          />
        ) : null}

        {/* REGISTRATION-PARTHA-CONTRACT-V1 (ONE-REGISTRATION-MODULE): the
            governed contract field sections (Title through Emergency) render
            from THE ONE shared module -- the same component the search /
            person-only registration host renders. Entry-point differences
            (prefill source, writer, chrome, and the primary name/phone
            read-back above) stay in this host adapter. */}
        <RegistrationContractSections
          visitFields={identityAsFields ? <section id="pim-sec-complaint" data-reg-field="reason_for_visit" data-testid={complaintOnFile !== null ? 'intake-complaint-on-file' : 'intake-complaint'}><span className="ac-wizard__label">Reason for visit{(registrationRequiredFields.includes('reason_for_visit') || reasonQuestion?.required) ? <span className="pim-req" aria-hidden="true"> *</span> : null}</span>{reasonInner}</section> : undefined}
          workspaceRegistration={identityAsFields}
          savedContactFields={recovery ? recovery.contacts ?? {
            alt_mobile:{state:'unavailable',value:null},alt_mobile_relation:{state:'unavailable',value:null},
            alt_mobile_relation_name:{state:'unavailable',value:null},emergency_contact_name:{state:'unavailable',value:null},
            emergency_contact_relation:{state:'unavailable',value:null},emergency_contact_phone:{state:'unavailable',value:null},
          } : undefined}
          choicePresentation={identityAsFields ? 'select' : 'chips'}
          values={registrationValues}
          onChange={onRegistrationChange}
          isRequired={(t) => registrationRequiredFields.includes(t)}
          prefill={{
            nameTitleOptions: recovery && nameTitle ? [...new Set([nameTitle,...(prefill?.nameTitleOptions ?? [])])] : prefill?.nameTitleOptions,
            relationOptions: prefill?.relationOptions,
            relationTitleOptions: recovery && relationTitle ? [...new Set([relationTitle,...(prefill?.relationTitleOptions ?? [])])] : prefill?.relationTitleOptions,
            occupationOptions: recovery && occupation ? [...new Set([occupation,...(prefill?.occupationOptions ?? [])])] : prefill?.occupationOptions,
            handlingCategories: prefill?.handlingCategories,
            stateDistricts: prefill?.stateDistricts,
          }}
          busy={submitting}
          ageTitle={voice.ageTitle}
          ageDisplay={ageDisplayText(ageView)}
          minorNote={!recovery && guardianIncomplete ? 'Complete guardian and consent details.' : null}
          genderRequired={genderRequired}
          genderHint={
            !identityAsFields && gender.trim() !== ''
              ? knownFieldConfirmHint(
                  ARRIVAL_GENDER_OPTIONS.find((g) => g.value === gender)
                    ?.label ?? gender,
                )
              : null
          }
          /* DESK identity-as-fields (founder visual verdict): the Patient
             details eyebrow + the Full name field (mockup First-name position)
             + the Mobile field (mockup Contact position). undefined on the
             tablet, so the module keeps its headerless split there. */
          patientSectionTitle={identityAsFields ? voice.detailsHeading : undefined}
          patientIdentityField={deskFullNameField}
          contactMobileField={deskMobileField}
          /* T4 (SHIPPED-WORK-TRUTH-CORRECTIVE-V1): the save-then-reopen
             truth -- a saved email announces itself (presence bit off the
             prefill wire; the address never re-rides, D4). */
          emailOnFile={prefill?.emailOnFile === true}
        />


        {/* F7 (AUG-12-WALK-BLOCKERS-V1): ONE reason section -- the chips off
            the org's complaint vocabulary + optional free text, carrying the
            questionnaire's reason_for_visit question as its SINGLE required
            signal (the duplicate "main reason" ask below is gone). FD-29: a
            desk-captured reason is CONFIRMED, never re-asked. */}
        {identityAsFields ? null : complaintOnFile !== null ? (
          // REGISTRATION-DEPLOYED-WALK-CLOSURE-V1 (Defect B2): no "Answered"
          // chip -- the "You told us: <reason>" line already communicates the
          // reason is on file (the mockup carries no status decoration).
          <IntakeQuestionSection
            id="pim-sec-complaint"
            title={voice.complaintTitle}
            testId="intake-complaint-on-file"
            fieldKey="reason_for_visit"
          >
            <p className="pim-hint" data-testid="intake-complaint-line">
              {complaintOnFileLine(complaintOnFile)}
            </p>
          </IntakeQuestionSection>
        ) : (
          <IntakeQuestionSection
            id="pim-sec-complaint"
            title={voice.complaintTitle}
            required={reasonQuestion !== null && reasonQuestion.required}
            answered={complaint.trim() !== ''}
            testId="intake-complaint"
            fieldKey="reason_for_visit"
          >
            {(prefill?.complaintCategories ?? []).length > 0 ? (
              <div className="ac-tristate" role="group" aria-label="Reason for visit">
                {(prefill?.complaintCategories ?? []).map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    className={
                      complaint === c.label
                        ? 'ac-tristate__option ac-tristate__option--on'
                        : 'ac-tristate__option'
                    }
                    aria-pressed={complaint === c.label}
                    onClick={() => setComplaint(c.label)}
                    data-testid={`intake-complaint-chip-${c.code}`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            ) : null}
            <input
              className="pim-input"
              aria-label="Reason for visit - your own words"
              placeholder={PATIENT_MODE_COMPLAINT_OTHER_PLACEHOLDER}
              value={complaint}
              maxLength={200}
              onChange={(e) => setComplaint(e.target.value)}
              style={{ marginTop: 'var(--ac-space-2)' }}
              data-testid="intake-complaint-text"
            />
          </IntakeQuestionSection>
        )}

        {/* D5 -- THE CLINICAL BLOCK IS GONE FROM REGISTRATION.
            "A few questions for the doctor" (when did it start /
            pain-discomfort / the rest of that group) used to render HERE, at
            the front desk, inside an administrative form. It now opens the
            doctor's Start Consultation instead
            (frontend/src/dashboard/doctor/ConsultIntakeQuestionsBlock.tsx),
            reading and writing the SAME answers through the SAME routes.
            RELOCATED, NEVER DELETED: the question DEFINITIONS are untouched
            org config (preconsult.question_set), so the future patient
            self-fill tier renders exactly this set when it lands. */}
      </fieldset>

      {/* October 10 frozen Patient arrival: optional privacy disclosures remain
          in the registration form. Guardian consent stays separately required. */}
      {prefill ? (
        identityAsFields ? (
          <section className="arrival-privacy-disclosures" data-testid="reg-group-consent" aria-label="Privacy and communication">
            <IntakeConsentBlock consent={prefill.consent} compact />
          </section>
        ) : (
          <IntakeConsentBlock consent={prefill.consent} />
        )
      ) : null}

      {/* T-4/T-5: the ANCHORED submit bar -- opaque, bordered, sticky;
          server errors live HERE, never floating mid-form.
          D3 -- ONE VALIDATION PATH: the bar carries THE STICKY NEEDS-LINE,
          the form's only user-facing validation surface. It names EVERY
          missing required field at once, in desk voice ("Registration still
          needs: City, Phone."), whether the gap was caught here or reported
          by the server's contract check (needsLineFromServer maps the server
          answer into the same sentence). There is no second error style and
          no per-field technical message: a raw wire key like
          a raw wire path can no longer reach a human (the guard
          tests/unit/registration-contract-guard.test.ts pins it). */}
      {isMinor ? <GuardianConsentFields value={guardian} onChange={patch=>setGuardian(value=>({...value,...patch}))} busy={submitting || recovery !== null}/> : null}

      <IntakeSubmitBar
        error={(recovery ? null : needsLine) ?? error ?? (identityAsFields && !identityChecked ? 'Check the selected patient identity before checking in.' : null)}
        minorNote={!recovery && guardianIncomplete ? 'Complete guardian and consent details.' : null}
        summaryLine={
          // WALK-3 (C2): the "your doctor will receive ..." reassurance is
          // patient copy -- it renders only on the patient voice, never on the
          // staff path.
          voice.showPresubmitSummary && missingCount === 0
            ? presubmitSummary({
                hasComplaint:
                  complaintOnFile !== null || complaint.trim() !== '',
                answerCount: Object.keys(answers).length,
              })
            : null
        }
        submitButton={
          <>{onBack ? <AcButton variant="secondary" onClick={onBack} disabled={submitting}>Back to patient</AcButton> : null}<AcButton
            /* F5: ONE PRIMARY ACTION -- the single submit owns completion
               and stays DISABLED until every required section is complete;
               its label names the ONE action and the live remaining count. */
            variant="primary"
            onClick={() => void (recovery ? recoverSavedRegistration() : onSubmitTap())}
            disabled={submitting || (recovery ? !recovery.canResume || !identityChecked : missingCount > 0 || guardianIncomplete || (voice.identityLayout === 'fields' && !identityChecked))}
            data-testid="intake-submit"
          >
            {submitting ? 'Saving...' : identityAsFields ? 'Save & check in' : submitLabelForMissing(missingCount)}
          </AcButton></>
        }
      />
    </>,
  );
}
